import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { Trash2, FolderX } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function FolderEditModal({ folderName, documents, open, onClose, onUpdate }) {
  const [newFolderName, setNewFolderName] = useState(folderName);
  const [selectedDocs, setSelectedDocs] = useState([]);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    setNewFolderName(folderName);
    setSelectedDocs([]);
  }, [folderName]);

  const handleRename = async () => {
    if (!newFolderName.trim()) {
      toast.error('Folder name cannot be empty');
      return;
    }

    setSaving(true);
    const formData = new FormData();
    formData.append('new_name', newFolderName);

    try {
      await axios.put(`${API}/folders/${folderName}`, formData);
      toast.success('Folder renamed successfully');
      onUpdate();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to rename folder');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteFolder = async () => {
    if (!window.confirm(`Delete entire folder "${folderName}" and all ${documents.length} documents? This action cannot be undone.`)) {
      return;
    }

    setDeleting(true);
    try {
      await axios.delete(`${API}/folders/${folderName}`);
      toast.success('Folder and all documents deleted');
      onUpdate();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete folder');
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedDocs.length === 0) {
      toast.error('Please select documents to delete');
      return;
    }

    if (!window.confirm(`Delete ${selectedDocs.length} selected document(s)? This cannot be undone.`)) {
      return;
    }

    setDeleting(true);
    const formData = new FormData();
    selectedDocs.forEach(id => formData.append('verification_ids', id));

    try {
      await axios.post(`${API}/folders/${folderName}/delete-documents`, formData);
      toast.success(`${selectedDocs.length} document(s) deleted successfully`);
      setSelectedDocs([]);
      onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete documents');
    } finally {
      setDeleting(false);
    }
  };

  const toggleDocSelection = (verificationId) => {
    setSelectedDocs(prev =>
      prev.includes(verificationId)
        ? prev.filter(id => id !== verificationId)
        : [...prev, verificationId]
    );
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Folder: {folderName}</DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Rename Folder */}
          <div className="space-y-3">
            <Label htmlFor="folder-name">Rename Folder</Label>
            <div className="flex gap-2">
              <Input
                id="folder-name"
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                className="flex-1"
              />
              <Button onClick={handleRename} disabled={saving}>
                {saving ? 'Saving...' : 'Rename'}
              </Button>
            </div>
          </div>

          {/* Multi-Delete Documents */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label>Select Documents to Delete</Label>
              {selectedDocs.length > 0 && (
                <span className="text-sm text-slate-600">{selectedDocs.length} selected</span>
              )}
            </div>

            <div className="border rounded-lg max-h-64 overflow-y-auto">
              {documents.map((doc) => (
                <div
                  key={doc.verification_id}
                  className="flex items-center gap-3 p-3 border-b last:border-b-0 hover:bg-slate-50"
                >
                  <input
                    type="checkbox"
                    checked={selectedDocs.includes(doc.verification_id)}
                    onChange={() => toggleDocSelection(doc.verification_id)}
                    className="w-4 h-4"
                  />
                  <div className="flex-1">
                    <p className="text-sm font-medium">{doc.document_id}</p>
                    <p className="text-xs text-slate-500">{doc.document_name}</p>
                  </div>
                </div>
              ))}
            </div>

            {selectedDocs.length > 0 && (
              <Button
                onClick={handleDeleteSelected}
                disabled={deleting}
                variant="outline"
                className="w-full text-red-600 hover:text-red-700 hover:bg-red-50"
              >
                {deleting ? 'Deleting...' : `Delete ${selectedDocs.length} Selected`}
              </Button>
            )}
          </div>

          {/* Delete Entire Folder */}
          <div className="pt-4 border-t">
            <Button
              onClick={handleDeleteFolder}
              disabled={deleting}
              variant="outline"
              className="w-full text-red-600 hover:text-red-700 hover:bg-red-50 flex items-center justify-center gap-2"
            >
              <FolderX className="w-4 h-4" />
              {deleting ? 'Deleting...' : `Delete Entire Folder (${documents.length} documents)`}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
